'use client'

import {
  LuChevronsUpDown,
  LuLogOut,
  LuSettings,
  LuTriangleAlert,
  LuWallet,
} from 'react-icons/lu'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@ui/components/shadcn/ui'

import { useAttention } from '@web-app/components/account/attention/useAttention'
import { useSignOut } from '@web-app/components/auth/useSignOut'
import { useUserData } from '@web-app/context/UserDataContext'
import { formatCurrency } from '@web-app/utils/format'

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
  }
}) {
  const { isMobile } = useSidebar()
  const pathname = usePathname()
  const { balance, user: account } = useUserData()
  const { signOut, isSigningOut } = useSignOut('NavUser')
  // Worst open issue: badge on the avatar, glyph on the settings item.
  const { severity } = useAttention()
  const badgeTone =
    severity === 'critical'
      ? 'bg-red-500 text-white'
      : 'bg-amber-400 text-amber-950'
  const iconTone = severity === 'critical' ? 'text-red-500' : 'text-amber-500'
  // Initials from the account name; the email's first letter as fallback.
  const initials =
    [account?.firstName?.[0], account?.lastName?.[0]]
      .filter(Boolean)
      .join('')
      .toUpperCase() || user.email.charAt(0).toUpperCase()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={pathname === '/dashboard/wallet'}
          className="border-primary justify-center border group-data-[collapsible=icon]:justify-start"
        >
          <Link href="/dashboard/wallet">
            <LuWallet />
            <span>Balance: {formatCurrency(balance?.total)}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="rounded-lg">
                <AvatarFallback className="rounded-lg text-sm font-semibold">
                  {initials}
                </AvatarFallback>
                {severity && (
                  <AvatarBadge
                    role="img"
                    aria-label="Action needed"
                    className={`ring-sidebar ${badgeTone}`}
                  >
                    <LuTriangleAlert />
                  </AvatarBadge>
                )}
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <LuChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="rounded-lg">
                  <AvatarFallback className="rounded-lg text-sm font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link href="/dashboard/account">
                  <LuSettings />
                  Account Settings
                  {severity && (
                    <LuTriangleAlert
                      aria-label="Action needed"
                      className={`ml-auto ${iconTone}`}
                    />
                  )}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => void signOut()}
              disabled={isSigningOut}
            >
              <LuLogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
